import { Channel, CrisRequestType } from 'app/constants';
import { LocationMatchType } from 'app/enum';
import { Cobrander } from 'app/model/cobrander';
import { ModelWithoutId, PreProdPattern } from 'app/type';

import { makePatternData, PatternData } from './pattern-data';
import { RestfulModel } from './restful-model';
import { MatchType } from '../constants/match-type';

export interface SeamPattern extends RestfulModel {
    type: CrisRequestType;
    data: PatternData;
    position: number;
    tupleCount?: number;
    tupleCountRel?: string;
    supersedes?: number;
    outputId?: number;
    noReplacement: boolean;
    viewOnly?: boolean;
    /**
     * Cobrander associated with the pattern.
     */
    cobrander?: Cobrander;
    opposingId?: string;
}

/**
 * Converts standard regex to ES supported format.
 *
 * Basically we convert the ES patterns to proper regexes, so when editing we need to de reversal.
 * Check out \App\Components\ES\RegExParser in backend for more info.
 *
 * @param pattern
 */
const fixRegex = (pattern: string): string => {
    if (!pattern) {
        return pattern;
    }

    if (!pattern.startsWith('^')) {
        // legacy patterns might not start with ^
        pattern = `.*${pattern}`;
    }

    if (!pattern.endsWith('$')) {
        // legacy patterns might not end with $
        pattern = `${pattern}.*`;
    }

    pattern = pattern.replace(/^\^/, '');
    pattern = pattern.replace(/\$$/, '');

    return pattern;
};

/**
 * Constructs a default object of above instance.
 *
 * We do not want the above to be a class because this data comes from server and when it does it does not
 * get instantiated to a class, it only has the properties. So we have this factory method for constructing
 * fresh instances locally, when we need them.
 */
export const makeSeamPattern = (sourcePattern?: PreProdPattern): ModelWithoutId<SeamPattern> => {
    if (!sourcePattern) {
        return {
            type: CrisRequestType.STORE,
            data: makePatternData(),
            position: 0,
            noReplacement: false,
            viewOnly: false,
        };
    }

    let operator: MatchType;
    let locationOperator: LocationMatchType;
    let patternCallback = (patternStr: string) => patternStr;

    switch (sourcePattern.locationOperator) {
        case 'LIKE':
            locationOperator = LocationMatchType.LIKE;
            break;
        case 'NOT LIKE':
            locationOperator = LocationMatchType.NOT_LIKE;
            break;
        case 'NOT RLIKE':
            locationOperator = LocationMatchType.NOT_REGEX;
            patternCallback = fixRegex;
            break;
        case 'EQUAL':
            operator = MatchType.REGEX;
            locationOperator = LocationMatchType.EQUAL;
            break;
        default:
            locationOperator = LocationMatchType.EQUAL;
            patternCallback = fixRegex;
    }
    switch (sourcePattern.matchMethod) {
        case 'LIKE':
            operator = MatchType.LIKE;
            break;
        case 'NOT LIKE':
            operator = MatchType.NOT_LIKE;
            break;
        case 'NOT RLIKE':
            operator = MatchType.NOT_REGEX;
            patternCallback = fixRegex;
            break;
        default:
            operator = MatchType.REGEX;
            patternCallback = fixRegex;
    }

    const pattern: ModelWithoutId<SeamPattern> = {
        position: 0,
        supersedes: sourcePattern.id,
        type: sourcePattern.type,
        data: {
            matchOperator: operator,
            locationOperator,
            midPattern: patternCallback(sourcePattern.mid),
            descPattern: patternCallback(sourcePattern.merchdesc),
            mccMatchOperator: sourcePattern.mccOperator,
            mccPattern: Array.isArray(sourcePattern.mcc)
                ? sourcePattern.mcc
                : sourcePattern.mcc
                ? [sourcePattern.mcc]
                : [],
            cityPattern: sourcePattern.city,
            statePattern: sourcePattern.state,
            zipPattern: sourcePattern.zip,
            countryPattern: '',
            subChannel: Channel.NONE,
            extractField: '',
            extractPattern: '',
        },
        noReplacement: false,
        viewOnly: false,
    };

    switch (sourcePattern.type) {
        case CrisRequestType.STORE:
            pattern.data.countryPattern = sourcePattern.country;
            break;
        case CrisRequestType.CHANNEL:
            pattern.data.subChannel = sourcePattern.subChannel;
            break;
        case CrisRequestType.LOCATION:
            pattern.data.extractField = sourcePattern.extractField;
            pattern.data.extractPattern = sourcePattern.extractPattern;
            break;
        default:
            throw new Error('Unknown PreProdPattern type');
    }

    return pattern;
};
